/**
 * RFC-1146 / Canvas UI 2.0 — binary star toggle for a single variant tile.
 *
 * Two states, not three: starred (the default) and archived. Clicking a starred
 * tile archives it, which moves it to the Archived tab; clicking an archived
 * tile restores it to Variants. The prior neutral → favourite → disliked cycle
 * is gone — see the Canvas UI 2.0 note in `variantKey.ts` for how the surviving
 * `variant_markers` values are reinterpreted.
 *
 * Visibility rule: a starred tile shows its star on hover only, so a fresh grid
 * (where everything is starred) stays visually clean. An archived tile always
 * shows its hollow star, so its state is legible at a glance while browsing the
 * Archived tab.
 *
 * Placement: the trailing (rightmost) slot of the top-right overlay cluster on
 * every variant tile — after download on both grids. This component renders
 * INLINE and carries no positioning of its own; the caller places it:
 *
 *   - image tile: VariantImageOverlayButtons' `trailingControls` slot →
 *     [expand, download, star].
 *   - video tile: VideoPreviewPlayer's `topTrailingControls` slot →
 *     [mute, download, star], stacked down the right edge (star on top) on a
 *     portrait tile.
 *
 * Joining the cluster's flex row, rather than taking its own absolutely
 * positioned box, keeps the clearance a layout fact: #3511 gave this button
 * `absolute bottom-2 left-2`, where it covered a video tile's play/pause.
 *
 * The star keeps its OWN visibility rule rather than inheriting the cluster's,
 * which is why it sits outside the cluster's hover group: an archived tile
 * must show its hollow star without hovering.
 */

import { memo, useCallback } from 'react'
import { Star } from 'lucide-react'
import { useTranslation } from 'react-i18next'

export interface VariantMarkerButtonProps {
  /** Whether this variant is currently archived. */
  archived: boolean
  /** Toggle. Receives the state being moved TO, so the caller never re-derives it. */
  onToggle: (archived: boolean) => void
  /**
   * Disabled for unmarkable entries (placeholders with no `local_path`), which
   * have no stable identity to key a marker on.
   */
  disabled?: boolean
  /** Variant index, for a stable test id. */
  variantIndex: number
}

const VariantMarkerButton = memo(function VariantMarkerButton({
  archived,
  onToggle,
  disabled = false,
  variantIndex,
}: VariantMarkerButtonProps) {
  const { t } = useTranslation('canvas')

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      // The tile itself is a click target that picks the variant. Archiving must
      // NOT also change the selection (markers are display-only, RFC-1146 §0.3).
      event.stopPropagation()
      event.preventDefault()
      if (!disabled) onToggle(!archived)
    },
    [archived, disabled, onToggle],
  )

  const title = disabled
    ? t('variantMarker.unavailable')
    : archived
      ? t('variantMarker.restore')
      : t('variantMarker.archive')

  // Plain template literal, NOT cn/twMerge: twMerge drops conflicting custom
  // utilities and breaks the class-based selectors these tests rely on — the
  // same reason VideoPreviewPlayer and the video version label avoid it
  // (see VideoVariantsTabContent's version-label comment).
  //
  // No `1 / zoom` transform anywhere here: the Variants tab is pinned to
  // scale=1 (UnifiedBottomPanel) and that size-match is intentional UX per
  // CLAUDE.md → Variants Tab Zoom.
  const visibility = archived
    ? 'opacity-100'
    : 'opacity-0 group-hover:opacity-100 focus-visible:opacity-100'

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      title={title}
      aria-label={title}
      aria-pressed={!archived}
      data-testid={`variant-marker-button-${variantIndex}`}
      data-archived={archived}
      className={`w-7 h-7 flex-shrink-0 flex items-center justify-center rounded-md bg-black/60 text-white transition-opacity duration-150 hover:bg-black/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-30 nodrag ${visibility}`}
    >
      <Star
        className={archived ? 'w-4 h-4' : 'w-4 h-4 text-amber-300'}
        fill={archived ? 'none' : 'currentColor'}
      />
    </button>
  )
})

VariantMarkerButton.displayName = 'VariantMarkerButton'

export { VariantMarkerButton }
